import { useRouter } from "next/router";
import { defaultProps } from "helpers/common";
import { Stack, StackProps, Typography, TypographyProps } from "@mui/material";
import { ArrowBack, Circle } from "@mui/icons-material";
import AppIconButton, { AppIconButtonProps } from "components/v2/atoms/app-icon-button/AppIconButton";

export interface AppNavStepsProps {
    stepCount: number;
    stackProps?: StackProps;
    currentStepIndex: number;
    circlesStackProps?: StackProps;
    backButtonProps?: AppIconButtonProps;
    heading?: string;
}

function AppNavSteps(props: AppNavStepsProps) {
    const router = useRouter();

    const stackProps = defaultProps(props.stackProps || {}, {
        sx: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
        },
    });

    const backButtonProps = defaultProps(props.backButtonProps || {}, {
        children: <ArrowBack />,
        sx: {
            color: "text.primary",
            p: 0,
            ml: -0.5,
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
        onClick() {
            router.back();
        },
    });

    const appNavStepCirclesProps: AppNavStepCirclesProps = {
        stepCount: props.stepCount,
        stackProps: props.circlesStackProps,
        currentStepIndex: props.currentStepIndex,
    };

    const hiddenButtonProps = defaultProps(props.backButtonProps || {}, {
        children: <ArrowBack />,
        sx: {
            visibility: "hidden",
        },
    });

    const headingProps: TypographyProps = {
        fontWeight: "600",
        fontSize: { xs: 20, sm: 20 },
        sx: {
            textAlign: "center",
        },
        children: props.heading,
    };

    const textStackProps = defaultProps(props.stackProps || {}, {
        sx: {
            gap: 0.5,
            flexDirection: "row",
            alignItems: "center",
        },
        children: <Typography {...headingProps} />,
    });

    return (
        <Stack {...stackProps}>
            <AppIconButton {...backButtonProps} />

            {props.heading ? (
                <Stack {...textStackProps}></Stack>
            ) : (
                <>
                    <AppNavStepCircles {...appNavStepCirclesProps} />
                </>
            )}
            <AppIconButton {...hiddenButtonProps} />
        </Stack>
    );
}

export default AppNavSteps;

export interface AppNavStepCirclesProps {
    stepCount: number;
    currentStepIndex: number;
    stackProps?: StackProps;
    preventFillPrev?: boolean;
}

export function AppNavStepCircles(props: AppNavStepCirclesProps) {
    const methods = {
        renderCircles() {
            return new Array(props.stepCount).fill(0).map((_, index) => {
                let color = "divider";

                if (!props.preventFillPrev && index <= props.currentStepIndex) {
                    color = "primary.main";
                } else {
                    color = index <= props.currentStepIndex ? "primary.main" : "divider";
                }

                return <Circle key={index} sx={{ fontSize: 16, color }} />;
            });
        },
    };

    const stackProps = defaultProps(props.stackProps || {}, {
        sx: {
            gap: 0.5,
            flexDirection: "row",
            alignItems: "center",
        },
        children: methods.renderCircles(),
    });

    return <Stack {...stackProps} />;
}
